/**
 * CameraBookmarksPanel — sidebar panel that lets users save and restore
 * named camera views for a deviation comparison.
 *
 * Persists via the comparison_router bookmark CRUD endpoints. On click,
 * applies the stored Forge camera state via `viewer.navigation.setView(...)`
 * and (optionally) restores cutting planes if they were captured at save time.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Bookmark, Plus, Trash2, ArrowUpRight } from 'lucide-react';

export default function CameraBookmarksPanel({ projectId, comparisonId, viewerRef }) {
  const [bookmarks, setBookmarks] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    if (!projectId || !comparisonId) { setBookmarks([]); return; }
    try {
      const r = await api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/bookmarks`);
      setBookmarks(r.data?.bookmarks || []);
    } catch (_) { setBookmarks([]); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectId, comparisonId]);

  const save = async () => {
    const v = viewerRef?.current;
    if (!v) { toast.error('Open the viewer first'); return; }
    if (!name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      const nav = v.navigation;
      const pos = nav.getPosition();
      const tgt = nav.getTarget();
      // upVector is exposed via navigation.getCameraUpVector() in the newer SDKs;
      // fall back to (0,0,1) for older builds.
      let up = { x: 0, y: 0, z: 1 };
      try { const u = nav.getCameraUpVector ? nav.getCameraUpVector() : null; if (u) up = { x: u.x, y: u.y, z: u.z }; } catch (_) {}
      const sectionPlanes = (v.getCutPlanes && v.getCutPlanes() || []).map(p => ({ x: p.x, y: p.y, z: p.z, w: p.w }));
      await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/bookmarks`, {
        name: name.trim(),
        position: { x: pos.x, y: pos.y, z: pos.z },
        target: { x: tgt.x, y: tgt.y, z: tgt.z },
        up,
        sectionPlanes: sectionPlanes.length ? sectionPlanes : null,
      });
      setName('');
      toast.success('Bookmark saved');
      refresh();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const apply = (bm) => {
    const v = viewerRef?.current;
    if (!v) { toast.error('Viewer not ready'); return; }
    try {
      v.navigation.setView(
        { x: bm.position.x, y: bm.position.y, z: bm.position.z },
        { x: bm.target.x, y: bm.target.y, z: bm.target.z },
      );
      if (bm.up && v.navigation.setCameraUpVector) {
        try { v.navigation.setCameraUpVector(new window.THREE.Vector3(bm.up.x, bm.up.y, bm.up.z)); } catch (_) {}
      }
      if (Array.isArray(bm.sectionPlanes) && bm.sectionPlanes.length && window.THREE) {
        const planes = bm.sectionPlanes.map(p => new window.THREE.Vector4(p.x, p.y, p.z, p.w));
        v.setCutPlanes(planes);
      }
      toast.success(`Restored: ${bm.name}`);
    } catch (e) { toast.error('Restore failed'); }
  };

  const remove = async (bmId) => {
    if (!window.confirm('Delete bookmark?')) return;
    try {
      await api.delete(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/bookmarks/${bmId}`);
      refresh();
    } catch (_) { toast.error('Delete failed'); }
  };

  if (!comparisonId) return null;
  return (
    <div className="rounded-lg p-2 space-y-2" style={{ background: '#0f1722', border: '1px solid #1f2937' }}
         data-testid="camera-bookmarks-panel">
      <button onClick={() => setOpen(o => !o)}
              className="flex items-center gap-1 w-full text-[11px] font-semibold opacity-90"
              data-testid="camera-bookmarks-toggle">
        <Bookmark className="w-3 h-3" />
        Camera Bookmarks ({bookmarks.length})
        <span className="ml-auto opacity-50 text-[10px]">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <>
          <div className="flex items-center gap-1">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Name (e.g. North column)"
                   className="flex-1 px-2 py-1 rounded text-xs"
                   style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                   data-testid="camera-bookmark-name" />
            <button onClick={save} disabled={busy || !name.trim()}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold"
                    style={{ background: '#06b6d4', color: '#000', opacity: (busy || !name.trim()) ? 0.5 : 1 }}
                    data-testid="camera-bookmark-save">
              <Plus className="w-3 h-3" /> Save view
            </button>
          </div>
          {bookmarks.length === 0 ? (
            <div className="text-[10px] opacity-50 px-1">No bookmarks yet — frame an interesting view and click Save.</div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {bookmarks.map(bm => (
                <div key={bm.id} className="flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                     style={{ background: '#0a0e14', border: '1px solid #1f2937' }}
                     data-testid={`camera-bookmark-${bm.id}`}>
                  <button onClick={() => apply(bm)} className="flex-1 text-left truncate hover:text-cyan-300"
                          data-testid={`camera-bookmark-apply-${bm.id}`}>
                    <ArrowUpRight className="w-2.5 h-2.5 inline mr-1 opacity-60" />
                    {bm.name}
                  </button>
                  <button onClick={() => remove(bm.id)} className="p-0.5 rounded hover:bg-red-900/30"
                          aria-label="Delete bookmark"
                          data-testid={`camera-bookmark-delete-${bm.id}`}>
                    <Trash2 className="w-2.5 h-2.5 opacity-60 hover:opacity-100 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
